/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable jsx-a11y/interactive-supports-focus */
import React, {
  useState, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Tooltip from 'components/core/Tooltip';
import InformationIcon from 'assets/icons/information';
import { ReactComponent as RefreshIcon } from 'assets/icons/refresh.svg';
import commonStyles from '../common.cssmodule.css';
import styles from './style.cssmodule.css';
import { ReactComponent as X } from '../icons/X.svg';
import { ReactComponent as MagnifyingGlassIcon } from '../icons/MagnifyingGlass.svg';
import { ReactComponent as ArrowIcon } from '../icons/Arrow.svg';

function createKeyFromOption(option) {
  let key = null;
  if (typeof option === 'string') {
    key = option;
  } else if (option) {
    key = option.id || option.value || `option-${option.name}`;
  }
  return key;
}

function Dropdown({
  fullWidth,
  hint,
  error,
  dotColor,
  name,
  label,
  required,
  tooltipId,
  inputIcon,

  canFilter,
  filter,
  filterOnChange,

  canSelectMultiple,
  handleClear,

  disabled,
  placeholder,

  selectedOption,

  options,
  onChange,
  renderOption,
  renderSelectedOption,
}) {
  const { formatMessage: t } = useIntl();

  const [isOpen, setIsOpen] = useState(false);

  /* Filter --- */
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const inputContainerRef = useRef(null);

  const handleSelectedOption = (newValue) => {
    if (!canSelectMultiple) setIsOpen(false);
    onChange(newValue);
  };

  useEffect(() => {
    const checkClickOutside = (event) => {
      if (listRef.current && inputContainerRef.current && !inputContainerRef.current.contains(event.target) && !listRef.current.contains(event.target)) {
        setIsOpen(false);
        filterOnChange('');
      }
    };

    document.addEventListener('click', checkClickOutside);
    return () => document.removeEventListener('click', checkClickOutside);
  }, []);

  // Fonction de rendu par défaut pour une option
  const defaultOptionRenderer = (option, onClick) => (
    <div key={createKeyFromOption(option)} className={styles.option} onClick={onClick}>
      {option.icon && <span>{option.icon}</span>}
      {option.displayElement || option.name || option}
    </div>
  );

  // Fonction de rendu personnalisée ou par défaut
  const RenderOption = renderOption || defaultOptionRenderer;

  const NoSuggestionsAvailable = null;

  /* --- Filter */

  const DotColor = dotColor && <div className={commonStyles.dotColor} style={{ backgroundColor: dotColor }} />;
  const Label = label ? <label htmlFor={name} className={commonStyles.label} >{`${label}${required ? ' *' : ''}`}</label> : null;

  const InformationTooltip = tooltipId && (
    <Tooltip content={t({ id: tooltipId })}>
      <InformationIcon className={commonStyles.infoIcon} />
    </Tooltip>
  );

  const Hint = (!error && hint)
    ? (
      <span className={commonStyles.hint}>
        {hint}
      </span>
    ) : null;

  const ErrorMessage = (
    <span className={commonStyles.error}>
      {error ? (
        <React.Fragment>
          <X />
          <span>{error}</span>
        </React.Fragment>
      ) : null}
    </span>
  );

  // Rendu de l'option sélectionnée dans l'input
  const renderSelectedContent = () => {
    if (!selectedOption) return '';

    if (renderSelectedOption) {
      return renderSelectedOption(selectedOption);
    }

    if (selectedOption.displayElement) {
      return (
        <div className={styles.selectedDisplay}>
          {selectedOption.icon && <span className={styles.selectedIcon}>{selectedOption.icon}</span>}
          {selectedOption.displayElement}
        </div>
      );
    }

    if (selectedOption && typeof selectedOption === 'string') {
      return <span>{selectedOption}</span>;
    }

    return selectedOption.name || '';
  };

  /* ============= */
  /* ||||||||||||| */
  /* ============= */
  return (
    <div className={commonStyles.field} style={{ gridColumn: fullWidth ? '1 / -1' : 'span 1' }}>
      {label && (
      <div className={commonStyles.fieldHeader}>
        {DotColor}
        {Label}
        {InformationTooltip}
      </div>
      )}
      <div className={`${styles.listContainer} ${isOpen ? 'open' : ''} ${handleClear ? 'withClearButton' : ''}`}>
        <div className={styles.list} ref={listRef}>
          {canFilter && (
            <div className={styles.filter}>
              <MagnifyingGlassIcon />
              <input tabIndex={-1} type="text" name="filter" id="filter" onChange={filterOnChange} value={filter} />
            </div>
          )}
          <React.Fragment>
            {options.length > 0
              ? options.map((option) => (
                <React.Fragment key={createKeyFromOption(option)}>
                  {RenderOption(option, () => handleSelectedOption(option))}
                </React.Fragment>
              ))
              : NoSuggestionsAvailable}
          </React.Fragment>
        </div>
        {/* Clear Button */}
        {handleClear && (
        <button
          type="button"
          onClick={handleClear}
          className={styles.buttonClear}
        >
          <RefreshIcon stroke="#2C3652" />
          <span>
            {t({ id: 'button.clear' })}
          </span>
        </button>
        )}
      </div>
      <div
        className={commonStyles.inputContainer}
        style={{ zIndex: 2, cursor: disabled ? 'not-allowed' : 'pointer' }}
        ref={inputContainerRef}
        onClick={() => {
          if (!disabled) {
            inputRef.current.focus();
            setIsOpen((v) => !v);
            filterOnChange('');
          }
        }}

      >
        {inputIcon}
        {typeof renderSelectedContent() === 'string' ? (
          <input
            type="text"
            ref={inputRef}
            className={styles.input}
            disabled={disabled}
            placeholder={placeholder}
            readOnly
            value={renderSelectedContent()}
          />
        ) : (
          <div ref={inputRef} style={{ flexGrow: 1 }}>
            {renderSelectedContent()}
          </div>
        )}
        {!disabled && (
        <ArrowIcon
          className={styles.arrow}
        />
        )}
      </div>
      {Hint}
      {ErrorMessage}
    </div>
  );
}

Dropdown.propTypes = {
  fullWidth: PropTypes.bool,
  hint: PropTypes.string,
  error: PropTypes.string,
  dotColor: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  tooltipId: PropTypes.string,
  inputIcon: PropTypes.node,

  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  handleClear: PropTypes.func,
  canSelectMultiple: PropTypes.bool,

  canFilter: PropTypes.bool,
  filter: PropTypes.string,
  filterOnChange: PropTypes.func,

  selectedOption: PropTypes.shape({
    name: PropTypes.string,
    icon: PropTypes.node,
    displayElement: PropTypes.node,
  }),
  options: PropTypes.array,
  onChange: PropTypes.func,
  renderOption: PropTypes.func,
  renderSelectedOption: PropTypes.func,
};

Dropdown.defaultProps = {
  fullWidth: false,
  hint: null,
  error: null,
  dotColor: null,
  label: null,
  required: false,
  tooltipId: null,
  inputIcon: null,
  handleClear: null,
  canSelectMultiple: false,

  canFilter: false,
  filter: null,
  // eslint-disable-next-line no-console
  filterOnChange: () => { console.warn('No change event handler for filtering'); },

  disabled: false,
  placeholder: null,
  selectedOption: null,
  options: [
    { id: '1', value: 'Item 1', name: 'Item 1' },
    { id: '2', value: 'Item 2', name: 'Item 2' },
    { id: '3', value: 'Item 3', name: 'Item 3' },
    { id: '4', value: 'Item 4', name: 'Item 4' },
  ],
  onChange: () => {},
  renderOption: null,
  renderSelectedOption: null,
};

export default Dropdown;



// WEBPACK FOOTER //
// ./components/inputs/Dropdown/Dropdown.jsx